import { installChartFetch, isChartCalculationRequest, isAyanamshaAwareRequest } from '../utils/chartCalculationTransport';
import { chartAyanamshaSettings } from '../utils/customAyanamsha';
import React, { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import type { AyanamshaMode } from './UserPreferencesContext';
import axios from 'axios';
import { STUDIO_API_BASE_URL as API_BASE_URL, studioApi, studioRequest } from '../api/studioClient';

// Types
interface Planet {
  name: string;
  longitude: number;
  latitude: number;
  distance: number;
  speed: number;
  house: number;
  bhavaChalitHouse?: number;
  sign: {
    number: number;
    name: string;
    degrees: number;
    element: string;
    quality: string;
    ruler: string;
  };
  degree: {
    degrees: number;
    minutes: number;
    seconds: number;
    formatted: string;
  };
  nakshatra: {
    number: number;
    name: string;
    lord: string;
    degrees: number;
    pada: number;
  };
  retrograde?: boolean;
}

interface House {
  longitude: number;
  sign: {
    number: number;
    name: string;
    degrees: number;
    element: string;
    quality: string;
    ruler: string;
  };
  degree: {
    degrees: number;
    minutes: number;
    seconds: number;
    formatted: string;
  };
  nakshatra: {
    number: number;
    name: string;
    lord: string;
    degrees: number;
    pada: number;
  };
}

interface BirthChart {
  _id?: string;
  chartId?: string;
  chartType?: string;
  horaryMeta?: any;
  birthDetails: {
    name: string;
    dateOfBirth: string;
    timeOfBirth: {
      hour: number;
      minute: number;
    };
    placeOfBirth: {
      city: string;
      state: string;
      country: string;
      latitude: number;
      longitude: number;
      timezone: number;
    };
    gender: string;
  };
  calculationData: {
    julianDay: number;
    ayanamsha: number;
    ayanamshaSettings?: import('../utils/customAyanamsha').AyanamshaSettings;
    ayanamshaMode?: AyanamshaMode;
    nodeMode?: 'true' | 'mean';
    calculatedAt: string;
  };
  planets: {
    [key: string]: Planet;
  };
  houses: {
    cusps: {
      [key: string]: House;
    };
    ascendant: House;
    midheaven: House;
    armc: number;
    vertex: number;
    equatorialAscendant: number;
    coAscendant: number;
    polarAscendant: number;
  };
  bhavaChalit?: {
    cusps: {
      [key: string]: House;
    };
    system: string;
  };
  nadiAnalysis: {
    cuspalInterlinks: any;
    planetarySignificators: any;
    eventAnalysis: any;
    generalPrediction: string;
  };
  chartSettings: {
    chartStyle: string;
    language: string;
    nodeMode?: 'true' | 'mean';
  };
  shareCode?: string;
  isPublic?: boolean;
}

interface Location {
  _id?: string;
  name: string;
  country: {
    code: string;
    name: string;
  };
  admin1: {
    code: string;
    name: string;
  };
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: {
    name: string;
    offset: number;
  };
  displayName: string;
}

interface ChartState {
  currentChart: BirthChart | null;
  userCharts: BirthChart[];
  locations: Location[];
  isLoading: boolean;
  error: string | null;
  locationSearchLoading: boolean;
}

const PREVIEW_CHART_STORAGE_KEY = 'kundlistarPreviewChart';

const loadPreviewChart = (): BirthChart | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(PREVIEW_CHART_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : null;
    return parsed?.birthDetails ? parsed : null;
  } catch (_) {
    return null;
  }
};

const storePreviewChart = (chart: BirthChart) => {
  if (typeof window === 'undefined') return;
  try {
    if (chart._id || chart.chartId) {
      window.sessionStorage.removeItem(PREVIEW_CHART_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(PREVIEW_CHART_STORAGE_KEY, JSON.stringify(chart));
  } catch (_) {
    // The in-memory preview remains available when storage is unavailable.
  }
};

type ChartAction =
  | { type: 'CALCULATION_START' }
  | { type: 'CALCULATION_SUCCESS'; payload: BirthChart }
  | { type: 'CALCULATION_FAILURE'; payload: string }
  | { type: 'LOAD_CHARTS_START' }
  | { type: 'LOAD_CHARTS_SUCCESS'; payload: BirthChart[] }
  | { type: 'LOAD_CHARTS_FAILURE'; payload: string }
  | { type: 'LOCATION_SEARCH_START' }
  | { type: 'LOCATION_SEARCH_SUCCESS'; payload: Location[] }
  | { type: 'LOCATION_SEARCH_FAILURE'; payload: string }
  | { type: 'SET_CURRENT_CHART'; payload: BirthChart }
  | { type: 'CLEAR_ERROR' };

interface ChartContextType {
  state: ChartState;
  currentChart: BirthChart | null;
  calculateChart: (chartData: any, options?: { trialAccessToken?: string }) => Promise<BirthChart>;
  calculateHoraryChart: (chartData: any) => Promise<BirthChart>;
  loadUserCharts: (page?: number, limit?: number, filters?: { type?: string; name?: string; date?: string }) => Promise<{ charts: BirthChart[]; pagination: any }>;
  deleteChart: (chartId: string) => Promise<void>;
  searchLocations: (query: string, country?: string) => Promise<Location[]>;
  getMajorCities: () => Promise<Location[]>;
  setCurrentChart: (chart: BirthChart) => void;
  clearError: () => void;
}

// Initial state
const initialState: ChartState = {
  currentChart: loadPreviewChart(),
  userCharts: [],
  locations: [],
  isLoading: false,
  error: null,
  locationSearchLoading: false
};

// Reducer
const chartReducer = (state: ChartState, action: ChartAction): ChartState => {
  switch (action.type) {
    case 'CALCULATION_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'CALCULATION_SUCCESS':
      return {
        ...state,
        isLoading: false,
        currentChart: action.payload,
        error: null
      };
    case 'CALCULATION_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'LOAD_CHARTS_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'LOAD_CHARTS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        userCharts: action.payload,
        error: null
      };
    case 'LOAD_CHARTS_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'LOCATION_SEARCH_START':
      return {
        ...state,
        locationSearchLoading: true
      };
    case 'LOCATION_SEARCH_SUCCESS':
      return {
        ...state,
        locationSearchLoading: false,
        locations: action.payload
      };
    case 'LOCATION_SEARCH_FAILURE':
      return {
        ...state,
        locationSearchLoading: false,
        error: action.payload
      };
    case 'SET_CURRENT_CHART':
      return {
        ...state,
        currentChart: action.payload
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Context
const ChartContext = createContext<ChartContextType | undefined>(undefined);

// Provider component
export const ChartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chartReducer, initialState);
  const activeChartRef = useRef(state.currentChart);
  activeChartRef.current = state.currentChart;
  useEffect(() => installChartFetch(() => activeChartRef.current, API_BASE_URL), []);
  useEffect(() => {
    const attachSnapshot = (config: import('axios').InternalAxiosRequestConfig) => {
      const snapshot = chartAyanamshaSettings(activeChartRef.current);
      if (isAyanamshaAwareRequest(config.url || '', API_BASE_URL)) config.headers.set('x-chart-calculation-version', '1');
      if (snapshot && !config.headers.has('x-chart-ayanamsha') && isChartCalculationRequest(config.url || '', API_BASE_URL)) config.headers.set('x-chart-ayanamsha', JSON.stringify(snapshot));
      return config;
    };
    const interceptor = axios.interceptors.request.use(attachSnapshot);
    const studioInterceptor = studioApi.interceptors.request.use(attachSnapshot);
    return () => { axios.interceptors.request.eject(interceptor); studioApi.interceptors.request.eject(studioInterceptor); };
  }, []);


  // Calculate chart function
  const calculateChart = async (chartData: any, options: { trialAccessToken?: string } = {}): Promise<BirthChart> => {
    try {
      dispatch({ type: 'CALCULATION_START' });
      
      const response = await studioApi.request({
        url: '/charts/calculate',
        method: 'POST',
        data: {
          ...chartData,
          createdFrom: chartData?.createdFrom || 'web',
        },
        headers: options.trialAccessToken
          ? { 'x-premium-trial-token': options.trialAccessToken }
          : undefined
      });
      const data = response.data?.data ?? response.data;
      const { chart, chartId } = data;
      const resolvedChart = chartId && !chart._id
        ? { ...chart, _id: chartId }
        : chart;
      
      dispatch({
        type: 'CALCULATION_SUCCESS',
        payload: resolvedChart
      });
      storePreviewChart(resolvedChart);
      
      return resolvedChart;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Chart calculation failed';
      dispatch({
        type: 'CALCULATION_FAILURE',
        payload: errorMessage
      });
      throw error;
    }
  };

  const calculateHoraryChart = async (chartData: any): Promise<BirthChart> => {
    try {
      dispatch({ type: 'CALCULATION_START' });

      const data = await studioRequest<any>({
        url: '/charts/horary',
        method: 'POST',
        data: chartData,
      }, 'Horary chart calculation failed');
      const { chart, chartId } = data;
      const resolvedChart = chartId && !chart._id
        ? { ...chart, _id: chartId }
        : chart;

      dispatch({
        type: 'CALCULATION_SUCCESS',
        payload: resolvedChart
      });
      storePreviewChart(resolvedChart);

      return resolvedChart;
    } catch (error: any) {
      const errorMessage = error.message || 'Horary chart calculation failed';
      dispatch({
        type: 'CALCULATION_FAILURE',
        payload: errorMessage
      });
      throw new Error(errorMessage);
    }
  };

  // Load user charts function
  const loadUserCharts = async (
    page: number = 1,
    limit: number = 10,
    filters: { type?: string; name?: string; date?: string } = {}
  ) => {
    try {
      dispatch({ type: 'LOAD_CHARTS_START' });
      
      const data = await studioRequest<any>({
        url: '/charts',
        method: 'GET',
        params: { page, limit, ...filters },
      }, 'Failed to load charts');
      const charts = data.charts || data.items || [];
      
      dispatch({
        type: 'LOAD_CHARTS_SUCCESS',
        payload: charts
      });
      
      return { charts, pagination: data.pagination || data.meta || {} };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load charts';
      dispatch({
        type: 'LOAD_CHARTS_FAILURE',
        payload: errorMessage
      });
      throw new Error(errorMessage);
    }
  };

  const deleteChart = async (chartId: string): Promise<void> => {
    try {
      await studioRequest({ url: `/charts/${chartId}`, method: 'DELETE' }, 'Failed to archive chart');
      dispatch({
        type: 'LOAD_CHARTS_SUCCESS',
        payload: state.userCharts.filter((chart) => chart._id !== chartId)
      });
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to archive chart';
      throw new Error(errorMessage);
    }
  };

  // Search locations function
  const searchLocations = async (query: string, country?: string): Promise<Location[]> => {
    try {
      dispatch({ type: 'LOCATION_SEARCH_START' });
      
      const params: any = { q: query };
      if (country) params.country = country;
      
      const data = await studioRequest<any>({
        url: '/locations/search',
        method: 'GET',
        params,
      }, 'Location search failed');
      const locations = data.cities || data.locations || data.items || [];
      
      dispatch({
        type: 'LOCATION_SEARCH_SUCCESS',
        payload: locations
      });
      
      return locations;
    } catch (error: any) {
      const errorMessage = error.message || 'Location search failed';
      dispatch({
        type: 'LOCATION_SEARCH_FAILURE',
        payload: errorMessage
      });
      throw new Error(errorMessage);
    }
  };

  // Get major cities function
  const getMajorCities = async (): Promise<Location[]> => {
    try {
      const data = await studioRequest<any>({
        url: '/locations/popular',
        method: 'GET',
      }, 'Failed to load major cities');
      const locations = data.cities || data.locations || data.items || [];
      
      dispatch({
        type: 'LOCATION_SEARCH_SUCCESS',
        payload: locations
      });
      
      return locations;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load major cities';
      throw new Error(errorMessage);
    }
  };

  // Set current chart function
  const setCurrentChart = (chart: BirthChart) => {
    storePreviewChart(chart);
    dispatch({
      type: 'SET_CURRENT_CHART',
      payload: chart
    });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const contextValue: ChartContextType = {
    state,
    currentChart: state.currentChart,
    calculateChart,
    calculateHoraryChart,
    loadUserCharts,
    deleteChart,
    searchLocations,
    getMajorCities,
    setCurrentChart,
    clearError
  };

  return (
    <ChartContext.Provider value={contextValue}>
      {children}
    </ChartContext.Provider>
  );
};

// Hook to use chart context
export const useChart = (): ChartContextType => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a ChartProvider');
  }
  return context;
};
